import { useState, SyntheticEvent, useEffect } from 'react';
import { MdAdd } from 'react-icons/md';

import { Alert, AlertColor, Backdrop, Box, CircularProgress, Snackbar } from '@mui/material';
import { softDelete } from 'api/api';
import CreateUpdateModalDialog from 'components/CreateUpdateModalDialog';
import IconDialog from 'components/IconDialog';
import Table from 'components/Table';
import { State } from 'types/list';
import { ListProps } from 'types/table';

import { IconContainer } from './styles';

interface handleOpenProps {
  action: 'create' | 'update';
  id?: string | number;
  isModalOpen?: boolean;
}

export default function List(props: ListProps) {
  const [refresh, setRefresh] = useState<boolean>(false);
  const [id, setId] = useState<string | number>();
  const [action, setAction] = useState<'create' | 'update'>('create');
  const [open, setOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteId, setDeleteId] = useState<string>('');
  const [state, setState] = useState<State>({
    openMsg: false,
    msg: '',
    vertical: 'bottom',
    horizontal: 'right',
    openBackdrop: false,
    alertSeverity: 'success' as AlertColor,
  });

  const { vertical, horizontal, openMsg, msg, openBackdrop } = state;

  const handleDelete = (codigo: string) => {
    setDeleteId(codigo);
    setOpenAlert(true);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleConfirmAlert = () => {
    setState({ ...state, openBackdrop: true });
    softDelete(props.table, deleteId).then((res) => {
      const data = res.data;
      if (!data.error) {
        setState({
          ...state,
          openMsg: true,
          msg: 'Registro excluído com sucesso!',
        });
        setRefresh(!refresh);
        setOpenAlert(false);
      } else {
        setState({ ...state, openBackdrop: false });
      }
    });
  };

  const handleCloseMsg = (event?: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setState({ ...state, openMsg: false, msg: '' });
  };

  const actions = [];

  const handleOpen = ({ action, id }: handleOpenProps) => {
    setOpen(true);
    setAction(action);
    if (id) {
      setId(id);
    }
  };

  const handleClose = async () => {
    setOpen(false);
  };

  if (!props.hideCreate) {
    actions.push({
      icon: () => (
        <IconContainer>
          {props.registerBtn ? (
            <>
              {props.registerBtn.icon}
              {props.registerBtn.text}
            </>
          ) : (
            <>
              <MdAdd fontSize={20} />
              Cadastrar
            </>
          )}
        </IconContainer>
      ),
      tooltip: 'Cadastrar',
      isFreeAction: true,
      onClick: () => handleOpen({ action: 'create' }),
    });
  }

  if (!props.hideCreate && props.secondRegisterBtn) {
    actions.push({
      icon: () => (
        <IconContainer>
          {props.secondRegisterBtn ? (
            <>
              {props.secondRegisterBtn.icon}
              {props.secondRegisterBtn.text}
            </>
          ) : (
            <>
              <MdAdd fontSize={20} />
              Cadastrar
            </>
          )}
        </IconContainer>
      ),
      tooltip: 'Cadastrar',
      isFreeAction: true,
      onClick: () => handleOpen({ action: 'create' }),
    });
  }

  if (!props.hideEdit) {
    actions.push({
      icon: 'edit',
      iconProps: { color: 'info' as any },
      tooltip: 'Editar',
      onClick: (event: any, rowData: any) => {
        handleOpen({ action: 'update', id: rowData[props.idColumn] });
      },
    });
  }

  if (!props.hideDelete) {
    actions.push({
      icon: 'delete',
      iconProps: { color: 'error' as any },
      tooltip: 'Excluir',
      onClick: (event: any, rowData: any) => {
        handleDelete(rowData[props.idColumn]);
      },
    });
  }

  useEffect(() => {
    if (!open) setRefresh(!refresh);
  }, [open]);

  return (
    <>
      <Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
          open={openBackdrop}
          onClick={() => setState({ ...state, openBackdrop: false })}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Table
          columns={props.columns}
          data={props.data}
          title={props.title}
          actions={actions}
          refresh={refresh}
          detailPanel={props?.detailPanel}
        />

        <IconDialog
          open={openAlert}
          onClose={handleCloseAlert}
          onConfirm={handleConfirmAlert}
          title="Excluir Registro"
          description="Deseja realmente excluir este registro?"
          variant="info"
          color="error"
        />

        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={openMsg}
          autoHideDuration={6000}
          key={vertical + horizontal}
          onClose={handleCloseMsg}
        >
          <Alert variant="filled" severity={state.alertSeverity}>
            {msg}
          </Alert>
        </Snackbar>
      </Box>

      {open && (
        <CreateUpdateModalDialog
          id={id}
          action={action}
          open={open}
          onClose={handleClose}
          table={props.table}
          idColumn={props.idColumn}
          getFields={props.getFields}
          state={state}
          setState={setState}
        />
      )}
    </>
  );
}
